import {buildTourProgressElement, postJson} from './product-tours.js'

export const BUG_REPORT_TOUR_EVENT = 'cpm:tour:report-a-bug'
const TOUR_ID = 'report-a-bug'
let activeTour = null

function bubbleElement(documentObject, selector) {
    return documentObject.getElementById('bubble-host')?.shadowRoot?.querySelector(selector) ?? null
}

function stepContent(documentObject, text, index, total) {
    const content = documentObject.createElement('div')
    content.append(buildTourProgressElement(index, total, documentObject))

    const body = documentObject.createElement('p')
    body.className = 'nurse-performance-tour-body'
    body.textContent = text
    content.append(body)

    return content
}

async function waitForBubble(browserWindow, documentObject, selector) {
    for (let attempt = 0; attempt < 50; attempt++) {
        const element = bubbleElement(documentObject, selector)
        if (element) return element
        await new Promise(resolve => browserWindow.setTimeout(resolve, 100))
    }

    return null
}

export async function startBugReportTour(browserWindow, documentObject, ShepherdLibrary) {
    if (activeTour || ShepherdLibrary.activeTour) return false

    const button = await waitForBubble(browserWindow, documentObject, '[data-bubble-bug-report]')
    const reportBug = browserWindow.Bubble?.instance?.reportBug
    if (!button || typeof reportBug !== 'function') return false

    const tour = new ShepherdLibrary.Tour({
        defaultStepOptions: {
            cancelIcon: {enabled: true},
            classes: 'nurse-performance-tour',
            modalOverlayOpeningPadding: 10,
            modalOverlayOpeningRadius: 16,
            scrollTo: {behavior: 'smooth', block: 'center'},
        },
        exitOnEsc: true,
        exitOnOverlayClick: true,
        useModalOverlay: true,
    })
    activeTour = tour
    documentObject.body.classList.add('bug-report-tour-active')
    let openedDraft = false

    const steps = [
        {
            id: 'bug-shortcut',
            selector: '[data-bubble-bug-report]',
            title: 'Report a problem from any page',
            text: 'Use this bug button when something in CPM does not work. Choose “Open a report” to see the form. CPM will capture the current page, but nothing is sent as a ticket until you choose Open issue.',
            button: 'Open a report',
        },
        {
            id: 'bug-subject',
            selector: '[data-bubble-bug-subject]',
            title: 'Check the report title',
            text: 'CPM fills in a title for you. You can change it to say what went wrong.',
            before: async () => {
                if (openedDraft && bubbleElement(documentObject, '[data-bubble-bug-subject]')) return
                let opened = false
                try {
                    opened = await reportBug()
                } catch {
                    tour.cancel()
                    return
                }
                if (!opened || !await waitForBubble(browserWindow, documentObject, '[data-bubble-bug-subject]')) {
                    tour.cancel()
                    return
                }
                openedDraft = true
            },
        },
        {
            id: 'bug-context',
            selector: '[data-bubble-bug-details-summary]',
            title: 'CPM gathers useful details',
            text: 'CPM includes the page, patient ID when present, and recent steps for the support team. You only see this short summary; the detailed log is kept out of your way.',
        },
        {
            id: 'bug-description',
            selector: '[data-bubble-bug-description]',
            title: 'Tell us what happened',
            text: 'In 3 to 5 sentences, say what you were trying to do, what you expected, and what happened instead. You do not need to copy the patient ID.',
        },
        {
            id: 'bug-screenshot',
            selector: '[data-bubble-bug-screenshots]',
            title: 'Check the screenshot',
            text: 'You may see “Attaching screenshot…” while CPM uploads the page image. When it appears, you can preview or remove it. If capture failed, use Capture full page to try again.',
        },
        {
            id: 'bug-send',
            selector: '[data-bubble-bug-submit]',
            title: 'Send it when you are ready',
            text: 'Choose Open issue to send the report to CPM support. This tour leaves the draft open and does not press that button for you. Choose Cancel in the form if you do not want to send it.',
        },
    ]

    steps.forEach((step, index) => {
        const first = index === 0
        const last = index === steps.length - 1
        tour.addStep({
            id: step.id,
            attachTo: {element: () => bubbleElement(documentObject, step.selector), on: 'right'},
            beforeShowPromise: step.before,
            buttons: [
                {action: () => first ? tour.cancel() : tour.back(), classes: 'shepherd-button-secondary', text: first ? 'Not now' : 'Back'},
                {action: () => last ? tour.complete() : tour.next(), text: last ? 'Done' : (step.button ?? 'Next')},
            ],
            text: stepContent(documentObject, step.text, index, steps.length),
            title: step.title,
        })
    })

    const cleanup = () => {
        documentObject.body.classList.remove('bug-report-tour-active')
        if (activeTour === tour) activeTour = null
    }

    tour.on('complete', () => {
        cleanup()
        postJson(browserWindow, `/tours/${TOUR_ID}/complete`)
    })
    tour.on('cancel', () => {
        cleanup()
        postJson(browserWindow, `/tours/${TOUR_ID}/dismiss`)
    })
    tour.start()

    return true
}

export function registerBugReportTour(browserWindow, documentObject, ShepherdLibrary) {
    const start = () => { void startBugReportTour(browserWindow, documentObject, ShepherdLibrary) }
    browserWindow.addEventListener(BUG_REPORT_TOUR_EVENT, start)

    return () => browserWindow.removeEventListener(BUG_REPORT_TOUR_EVENT, start)
}
